import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { AppSidebar } from '@/components/layout/sidebar'
import { SiteHeader } from '@/components/layout/site-header'
import { LazyCommandPalette } from '@/components/layout/lazy-command-palette'
import { TaskDetailsSheet } from '@/components/tasks/task-details-sheet'
import { MeetingDetailsSheet } from '@/components/meetings/meeting-details-sheet'
import { CreateTaskDialog } from '@/components/tasks/create-task-dialog'
import { CreateMeetingDialog } from '@/components/meetings/create-meeting-dialog'
import { useTaskStore } from '@/stores/task-store'
import { useMeetingStore } from '@/stores/meeting-store'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { redirectIfUnauthenticated } from '@/lib/auth-redirect'
import { useSocket } from '@/hooks/use-socket'
import { useChatHydrate } from '@/hooks/chats/use-chat-hydrate'
import { useChatUnreadSocketSync } from '@/hooks/chats/use-chat-unread-socket-sync'
import { useChatOutbox } from '@/hooks/chats/use-chat-outbox'
import { useTaskSocketSync } from '@/hooks/use-task-socket-sync'
import { useMeetingSocketSync } from '@/hooks/use-meeting-socket-sync'
import { rehydrateMeetingReminders } from '@/lib/meeting-reminders'
import { rehydrateTaskReminders } from '@/lib/task-reminders'
import { useContactHydrate } from '@/hooks/use-contact-hydrate'
import { useCallSignaling } from '@/hooks/calls/use-call-signaling'
import { CallOverlay } from '@/components/chats/call/call-overlay'
import { useCommandPalette } from '@/hooks/use-command-palette'
import { useFCMListener, useFCMBackgroundSync, useNotificationSync } from '@/hooks/notification'
import { useIsTablet } from '@/hooks/use-mobile'
import { useNotificationWarnings } from '@/hooks/notification'
import { useSubscriptionWarning } from '@/hooks/use-subscription-warning'
import { WarningBanner, type Warning } from '@/components/layout/warning-banner'
import { OvertimeRequestDialog } from '@/components/layout/overtime-request-dialog'
import { useOvertimeAlert } from '@/hooks/use-overtime-alert'
import { useAuthStore } from '@/stores/auth-store'
import { useRBAC } from '@/hooks/use-rbac'
import { writeNotificationIdentity } from '@/lib/notification-identity'

export const Route = createFileRoute('/(app)')({
  beforeLoad: redirectIfUnauthenticated,
  component: AppLayout,
})

function AppLayout() {
  const isChatEnabled = import.meta.env.VITE_FEATURE_CHAT === 'true';

  // Initialize socket and hydrate chat store only when chat feature is enabled
  useSocket({ initializeConnection: isChatEnabled });
  useChatHydrate({ enabled: isChatEnabled });

  // Keep the Tasks/Meetings unread badges live from the chat socket. The badge
  // counts read the chat.list cache; this feeds that cache from socket message +
  // receipt events, so visiting those pages no longer re-fetches chat-list-socket
  // (HTTP stays the baseline + socket-down fallback — see useChatHydrate).
  useChatUnreadSocketSync({ enabled: isChatEnabled });

  // Retry unsent / failed chat messages whenever the socket (re)connects.
  useChatOutbox({ enabled: isChatEnabled });

  // Tasks share the chat socket connection. When chat is enabled we listen for
  // `task-socket-add` / `task-socket-update` and merge them into the local store
  // so cross-platform mutations land in real time (no 10s poll lag).
  useTaskSocketSync({ enabled: isChatEnabled });

  // Meetings ride the same socket — `meeting-socket-add` /
  // `meeting-socket-update` plus the generic `event_data` channel (which
  // carries accept-reject and status-update broadcasts from iOS/Android).
  useMeetingSocketSync({ enabled: isChatEnabled });

  // Task/meeting mute caches are hydrated per-route (Tasks, Meetings, Today)
  // where those cards render — not globally — so unrelated pages don't fetch
  // `get-mute-management`. The persisted mute stores keep icons/suppression
  // correct everywhere; only the background revalidation is route-scoped.

  // Personal per-meeting reminders are stored in localStorage. Re-schedule
  // the setTimeout for every pending one when the user lands on any app
  // route, so reloads or tab-restarts don't lose their reminders. Mirrors
  // iOS `UNUserNotificationCenter` rescheduling on app launch.
  useEffect(() => {
    rehydrateMeetingReminders();
    rehydrateTaskReminders();
  }, []);

  // Hydrate the locally-cached (IndexedDB-persisted) contact store at startup
  // and keep it synced — every picker then reads it instantly and offline.
  useContactHydrate();

  // Listen for incoming voice/video calls (calls share the chat socket)
  useCallSignaling(isChatEnabled);

  // Listen for FCM foreground messages
  useFCMListener();

  // Sync background notifications from IndexedDB
  useFCMBackgroundSync();

  // Sync notification store with browser permission state
  useNotificationSync();

  // Mirror the routing identity (user id + company role) into IndexedDB so the
  // Firebase service worker can deep-link background-notification clicks the
  // same way the in-app panel does (1:1 chat rooms + role-based approvals).
  const notifUserId = useAuthStore((s) => s.user?.id ?? null);
  const { roleLevel: notifRoleLevel } = useRBAC();
  useEffect(() => {
    void writeNotificationIdentity({
      currentUserId: notifUserId,
      companyRoleId: notifRoleLevel,
    });
  }, [notifUserId, notifRoleLevel]);

  // Get notification warnings
  const notificationWarnings = useNotificationWarnings();

  // The subscription notice. Appended after the notification warnings so it
  // sits underneath them when both apply -- enabling notifications is a one-tap
  // fix, subscribing is not, so the cheaper action stays on top. WarningBanner's
  // own spacing provides the gap between them.
  const subscriptionWarnings = useSubscriptionWarning();

  // Overtime nudge — same banner strip as the notification warning, and
  // mounted here so it shows on every app route (native shows its band on
  // Today / Chats / Office alike). Last in the strip: it is a transient prompt,
  // not a standing account problem like the two above it.
  const overtime = useOvertimeAlert();
  const [overtimeFormOpen, setOvertimeFormOpen] = useState(false);

  const warnings = useMemo<Warning[]>(() => {
    const list: Warning[] = [...notificationWarnings, ...subscriptionWarnings];
    if (overtime.active) {
      list.push({
        id: 'overtime-active',
        type: 'success',
        title: overtime.companyName
          ? `Doing overtime at ${overtime.companyName}?`
          : 'Doing overtime?',
        message:
          'Send an overtime request to your reporting manager for approval.',
        action: { label: 'Request', onClick: () => setOvertimeFormOpen(true) },
        dismissible: true,
        onDismiss: overtime.dismiss,
      });
    }
    return list;
  }, [notificationWarnings, subscriptionWarnings, overtime]);

  // Command palette state
  const { open, setOpen } = useCommandPalette();
  const isTablet = useIsTablet();
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);
  const setSelectedTaskId = useTaskStore((s) => s.setSelectedTaskId);
  const selectedMeetingId = useMeetingStore((s) => s.selectedMeetingId);
  const setSelectedMeetingId = useMeetingStore((s) => s.setSelectedMeetingId);
  // `meetingToEdit` lives in the meeting store so the right-click context
  // menu on a meeting card can open the edit dialog without prop drilling.
  const meetingForEditDialog = useMeetingStore((s) => s.meetingToEdit);
  const setMeetingForEditDialog = useMeetingStore((s) => s.setMeetingToEdit);
  // `taskToEdit` lives in the task store (mirrors `meetingToEdit`) so the Today
  // timeline's right-click context menu can open the edit dialog without prop
  // drilling. The TaskDetailsSheet's "Edit" button also routes through it.
  const taskForEditDialog = useTaskStore((s) => s.taskToEdit);
  const setTaskForEditDialog = useTaskStore((s) => s.setTaskToEdit);

  return (
    <SidebarProvider defaultOpen={!isTablet}>
      <AppSidebar />
      {/* min-w-0 lets the content area shrink within the sidebar flex row instead
          of growing to its content's intrinsic width — without it, wide pages
          (e.g. the attendance timeline's big heading + 8 stat cards) push the
          whole content past the viewport and clip on the right. */}
      <SidebarInset className="min-w-0">
        <SiteHeader onSearchClick={() => setOpen(true)} />
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Sticky Warning Banner */}
          {warnings.length > 0 && (
            <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
              <div className="mx-auto w-full max-w-7xl px-4 py-3">
                <WarningBanner warnings={warnings} />
              </div>
            </div>
          )}
          
          <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-x-clip p-4">
            <div className="@container/main min-w-0 min-h-[100vh] flex-1 rounded-xl md:min-h-min">
              <div className="flex min-w-0 flex-col gap-4 overflow-x-clip p-4">
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>
      <LazyCommandPalette open={open} onOpenChange={setOpen} />
      <OvertimeRequestDialog
        open={overtimeFormOpen}
        onOpenChange={setOvertimeFormOpen}
        companyId={overtime.companyId ?? undefined}
        dayCheckInTimeMs={overtime.dayCheckInTimeMs}
      />
      <TaskDetailsSheet
        open={selectedTaskId != null}
        onOpenChange={(open) => !open && setSelectedTaskId(null)}
        taskId={selectedTaskId}
        onRequestEditTask={(task) => setTaskForEditDialog(task)}
      />
      <MeetingDetailsSheet
        open={selectedMeetingId != null}
        onOpenChange={(open) => !open && setSelectedMeetingId(null)}
        meetingId={selectedMeetingId}
        onRequestEditMeeting={(meeting) => setMeetingForEditDialog(meeting)}
      />
      <CreateTaskDialog
        open={taskForEditDialog != null}
        onOpenChange={(o) => {
          if (!o) setTaskForEditDialog(null);
        }}
        taskToEdit={taskForEditDialog}
      />
      <CreateMeetingDialog
        open={meetingForEditDialog != null}
        onOpenChange={(o) => {
          if (!o) setMeetingForEditDialog(null);
        }}
        meetingToEdit={meetingForEditDialog}
      />
      {isChatEnabled && <CallOverlay />}
    </SidebarProvider>
  )
}
